import { createTrancheEditRoute } from 'adminConstants/routes';
import { usePageMessages } from 'components/new/Page/usePageMessages';
import { FundStatus } from 'further-types/fund';
import useListPendingAdvisedForFund from 'hooks/data/investment/useListPendingAdvisedForFund';
import api, { ApiInputs } from 'lib/trpcClient';
import { UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { mapFormValuesToPayload } from '../mapping';
import { TrancheData } from '../types';
import {
  TrancheFormInput,
  TrancheFormValues,
  trancheFormSchema,
  tranchePublishableSchema,
} from '../useTrancheForm';

type Params = {
  form: UseFormReturn<TrancheFormInput, unknown, TrancheFormValues>;
  tranche: TrancheData | undefined;
  id: string;
  usesDealDocumentation: boolean;
  canEdit: boolean;
  create: ReturnType<typeof api.fund.create.useMutation>;
  update: ReturnType<typeof api.fund.update.useMutation>;
  pendingAdvised: ReturnType<typeof useListPendingAdvisedForFund>;
  closeTrancheDisclosure: {
    stageAction: (data: {
      payload: ApiInputs['fund']['update'];
      targetFundId?: string | null;
    }) => void;
  };
  updateFeeManagement: {
    stageAction: (data: { payload: ApiInputs['fund']['update'] }) => void;
  };
  addMessage: ReturnType<typeof usePageMessages>['addMessage'];
  removeMessage: ReturnType<typeof usePageMessages>['removeMessage'];
};

const useHandleTrancheSubmit = ({
  form,
  tranche,
  id,
  usesDealDocumentation,
  canEdit,
  create,
  update,
  pendingAdvised,
  closeTrancheDisclosure,
  updateFeeManagement,
  addMessage,
  removeMessage,
}: Params) => {
  const history = useHistory();

  const showFormErrors = () =>
    addMessage({
      key: 'form-errors',
      variant: 'error',
      centered: true,
      content:
        'One or more fields have errors, please correct them and try again.',
    });

  const validatePublishability = (data: TrancheFormValues): boolean => {
    const result = tranchePublishableSchema.safeParse({
      ...data,
      usesDealDocumentation,
    });
    if (result.success) return true;

    const isFormField = (
      field: string | number,
    ): field is keyof TrancheFormInput =>
      typeof field === 'string' && field in trancheFormSchema.shape;

    result.error.issues.forEach((issue) => {
      const field = issue.path[0];
      if (isFormField(field)) {
        form.setError(field, { message: issue.message });
      }
    });

    showFormErrors();
    return false;
  };

  const handleUpdate = async (fullPayload: ApiInputs['fund']['update']) => {
    const isClosingTranche =
      fullPayload.fundStatus === FundStatus.Closed &&
      tranche?.fundStatus !== FundStatus.Closed;

    if (isClosingTranche) {
      const pendingData =
        pendingAdvised.data ?? (await pendingAdvised.refetch())?.data;
      if (pendingData && pendingData.count > 0) {
        closeTrancheDisclosure.stageAction({
          payload: fullPayload,
          targetFundId: null,
        });
        return;
      }
    }

    const { dirtyFields } = form.formState;
    const feeFieldsDirty =
      'defaultCoreFee' in dirtyFields || 'defaultPerformanceFee' in dirtyFields;
    if (feeFieldsDirty && (tranche?.investments ?? []).length > 0) {
      updateFeeManagement.stageAction({ payload: fullPayload });
      return;
    }

    update.mutate(fullPayload);
  };

  const handleCreate = (payload: Omit<ApiInputs['fund']['update'], 'id'>) => {
    const { fundStatus, ...rest } = payload;
    if (fundStatus === FundStatus.Open) return;
    create.mutate(
      { ...rest, fundStatus },
      {
        onSuccess: (created) => {
          history.push(createTrancheEditRoute(created.id, true));
        },
      },
    );
  };

  const handleSave = async () => {
    if (!canEdit) return;
    await form.handleSubmit(async (data) => {
      removeMessage('form-errors');

      if (
        data.fundStatus === FundStatus.Open &&
        !validatePublishability(data)
      ) {
        return;
      }

      const payload = mapFormValuesToPayload(data);
      if (id) {
        await handleUpdate({ id, ...payload });
      } else {
        handleCreate(payload);
      }
    }, showFormErrors)();
  };

  return handleSave;
};

export default useHandleTrancheSubmit;
